import { useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Lock } from "@fleet/icons";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/providers/i18n-provider";
import {
  clearPin,
  hasPinConfigured,
  isValidPinFormat,
  PIN_MAX_LENGTH,
  savePin,
  verifyPin,
} from "../pin-lock";

type PinDialogMode = "setup" | "change" | "disable";

/**
 * Profile section: local 4-6 digit PIN (SHA-256 hash + salt in
 * localStorage of THIS device; never synced). Startup gate
 * lives in `pin-gate.tsx` (mounted by BootManager).
 *
 * Biometrics extension point: see header of `../pin-lock.ts`.
 */
export function PinSettingsSection() {
  const { t } = useI18n();

  const [enabled, setEnabled] = createSignal(hasPinConfigured());
  const [currentPin, setCurrentPin] = createSignal("");
  const [newPin, setNewPin] = createSignal("");
  const [confirmPin, setConfirmPin] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);

  // Dialog addressable by URL (`/settings/finance?pin=setup|change|disable`).
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });
  const mode = (): PinDialogMode | null => {
    const p = rawSearch().pin;
    return p === "setup" || p === "change" || p === "disable" ? p : null;
  };
  const setMode = (next: PinDialogMode | null) => {
    void navigate({
      to: "/settings/finance",
      replace: true,
      search: () => (next ? { pin: next } : {}),
    });
  };
  // Clears fields upon opening a new dialog (also when pasting URL).
  let lastMode: PinDialogMode | null = null;
  createEffect(() => {
    const m = mode();
    if (m && m !== lastMode) {
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
      setError(null);
    }
    lastMode = m;
  });

  const openDialog = (next: PinDialogMode) => {
    setMode(next);
  };

  const needsCurrent = () => mode() === "change" || mode() === "disable";
  const needsNew = () => mode() === "setup" || mode() === "change";

  const handleConfirm = async () => {
    if (busy()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (needsCurrent() && !(await verifyPin(currentPin()))) {
        setError(t(msg`Current PIN is incorrect.`));
        return;
      }
      if (needsNew()) {
        if (!isValidPinFormat(newPin())) {
          setError(t(msg`PIN must be 4-6 digits.`));
          return;
        }
        if (newPin() !== confirmPin()) {
          setError(t(msg`PINs do not match.`));
          return;
        }
        await savePin(newPin());
        setEnabled(true);
      } else {
        clearPin();
        setEnabled(false);
      }
      setMode(null);
    } finally {
      setBusy(false);
    }
  };

  const dialogTitle = () => {
    switch (mode()) {
      case "setup": {
        return t(msg`Set up PIN`);
      }
      case "change": {
        return t(msg`Change PIN`);
      }
      default: {
        return t(msg`Remove PIN`);
      }
    }
  };

  const pinInputClass =
    "rounded-md border border-border/60 bg-transparent px-3 py-2 text-sm tracking-[0.3em] text-foreground";

  return (
    <Card>
      <CardHeader class="flex flex-row items-center gap-2">
        <Lock class="h-5 w-5 text-primary" />
        <CardTitle>{t(msg`App Lock`)}</CardTitle>
      </CardHeader>
      <CardContent class="space-y-3">
        <p class="text-sm text-muted-foreground">
          {t(
            msg`Protect this device with a 4-6 digit PIN. It is stored only on this device and asked when the app starts.`,
          )}
        </p>
        <Show
          when={enabled()}
          fallback={
            <Button
              variant="outline"
              onClick={() => {
                openDialog("setup");
              }}
            >
              {t(msg`Set up PIN`)}
            </Button>
          }
        >
          <div class="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                openDialog("change");
              }}
            >
              {t(msg`Change PIN`)}
            </Button>
            <Button
              variant="outline"
              class="text-destructive"
              onClick={() => {
                openDialog("disable");
              }}
            >
              {t(msg`Remove PIN`)}
            </Button>
          </div>
        </Show>
      </CardContent>

      <Dialog
        open={mode() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMode(null);
          }
        }}
        contentClass="max-w-sm p-0"
      >
        <DialogHeader size="md">
          <DialogTitle>{dialogTitle()}</DialogTitle>
        </DialogHeader>
        <form
          class="flex flex-col gap-3 p-4 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleConfirm();
          }}
        >
          <Show when={needsCurrent()}>
            <label class="flex flex-col gap-1 text-xs text-muted-foreground">
              {t(msg`Current PIN`)}
              <input
                type="password"
                inputmode="numeric"
                autocomplete="off"
                maxlength={PIN_MAX_LENGTH}
                value={currentPin()}
                onInput={(e) => {
                  setCurrentPin(e.currentTarget.value.replaceAll(/\D/g, ""));
                }}
                class={pinInputClass}
              />
            </label>
          </Show>
          <Show when={needsNew()}>
            <label class="flex flex-col gap-1 text-xs text-muted-foreground">
              {t(msg`New PIN (4-6 digits)`)}
              <input
                type="password"
                inputmode="numeric"
                autocomplete="off"
                maxlength={PIN_MAX_LENGTH}
                value={newPin()}
                onInput={(e) => {
                  setNewPin(e.currentTarget.value.replaceAll(/\D/g, ""));
                }}
                class={pinInputClass}
              />
            </label>
            <label class="flex flex-col gap-1 text-xs text-muted-foreground">
              {t(msg`Confirm new PIN`)}
              <input
                type="password"
                inputmode="numeric"
                autocomplete="off"
                maxlength={PIN_MAX_LENGTH}
                value={confirmPin()}
                onInput={(e) => {
                  setConfirmPin(e.currentTarget.value.replaceAll(/\D/g, ""));
                }}
                class={pinInputClass}
              />
            </label>
          </Show>

          <Show when={error()}>
            <p class="text-xs text-destructive">{error()}</p>
          </Show>

          <Button
            type="submit"
            disabled={busy()}
            variant={mode() === "disable" ? "destructive" : "default"}
            class="w-full"
          >
            {mode() === "disable" ? t(msg`Remove PIN`) : t(msg`Save PIN`)}
          </Button>
        </form>
      </Dialog>
    </Card>
  );
}
